import React, {
    FC, memo, useEffect, useRef, useState,
} from 'react';

import classNames from 'classnames';

import { PdfViewerClient } from '../client/pdfViewerClient';
import { THUMBNAIL_FRAME, THUMBNAIL_WIDTH } from '../constants';
import { drawBitmap } from '../helpers/bitmap';
import { Observe } from '../hooks/useSharedObserver';
import { PageInfo } from '../types';
import styles from '../PdfTileViewer.module.scss';

interface ThumbnailProps {
    index: number;
    page: PageInfo;
    selected: boolean;
    isPaneActive: boolean;
    client: PdfViewerClient;
    observe: Observe;
    onSelect: (index: number) => void;
}

const Thumbnail: FC<ThumbnailProps> = ({
    index, page, selected, isPaneActive, client, observe, onSelect,
}) => {
    const rootRef = useRef<HTMLDivElement>(null);
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => observe(rootRef.current, setIsVisible), [observe]);

    useEffect(() => {
        if (!isVisible) {
            return undefined;
        }
        const controller = new AbortController();
        const canvas = canvasRef.current;
        client.fetchThumbnail(index, THUMBNAIL_WIDTH, controller.signal)
            .then((bitmap) => {
                if (!controller.signal.aborted) {
                    drawBitmap(canvas, bitmap);
                }
                client.release(bitmap);
            })
            .catch(() => undefined);
        return () => {
            controller.abort();
            canvas.width = 0;
        };
    }, [isVisible, client, index]);

    useEffect(() => {
        if (selected && isPaneActive) {
            rootRef.current?.scrollIntoView?.({ block: 'nearest' });
        }
    }, [selected, isPaneActive]);

    return (
        <div
            ref={rootRef}
            className={classNames(styles.thumbnail, { [styles.selected]: selected })}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(index)}
            onKeyDown={(event) => event.key === 'Enter' && onSelect(index)}
        >
            <div
                className={styles['thumbnail-frame']}
                style={{
                    width: THUMBNAIL_WIDTH + THUMBNAIL_FRAME,
                    height: Math.round((page.h * THUMBNAIL_WIDTH) / page.w) + THUMBNAIL_FRAME,
                }}
            >
                <canvas ref={canvasRef} style={{ width: THUMBNAIL_WIDTH }} />
            </div>
            <div className={styles['thumbnail-number']}>{index + 1}</div>
        </div>
    );
};

export default memo(Thumbnail);
